import { useTheme } from '@mui/material';
import { useMemo } from 'react';
import { getBorderMockJSS } from '../../../utils/getBorderMock.js';
import 'date-fns';
import { WarningLevel } from '../types.js';

/* eslint-disable @typescript-eslint/ban-ts-comment */
const getTextColor = ({ theme, modifiedManually, isOpen, isDisabled, onHover, warningLevel, }) => {
    switch (true) {
        case modifiedManually && !isOpen:
            return theme.palette.warning.main;
        case warningLevel && !isOpen:
            return theme.palette[warningLevel].main;
        case isDisabled:
            return theme.palette.N30.main;
        default:
            return theme.palette[onHover ? 'primary' : 'N10'].main;
    }
};
const getPlaceholderStyle = ({ theme, modifiedManually, isOpen, isDisabled, warningLevel, }) => {
    if (isOpen) {
        if (warningLevel) {
            return {
                color: `${theme.palette[warningLevel].main} !important`,
                fontStyle: 'italic !important',
            };
        }
        return {
            color: `${theme.palette.N30.main} !important`,
            fontStyle: 'italic !important',
        };
    }
    return { color: getTextColor({ theme, modifiedManually, isOpen, isDisabled, warningLevel }) };
};
const SELECT_HEIGHTS = {
    small: 20,
    medium: 30,
    large: 36,
};
const getHeight = (isMultiLine, size, heightToReduce = 0) => isMultiLine ? 'auto' : SELECT_HEIGHTS[size] - heightToReduce;
const getSelectStyles = ({ theme, size = 'medium', error, isOpen, withBorder, inactive, modifiedManually, isDisabled, containsInvalidValue, isMultiLine = false, warning, }) => {
    const warningLevel = warning ? WarningLevel.Warning : error ? WarningLevel.Error : undefined;
    return {
        container: provided => (Object.assign(Object.assign({}, provided), { transition: `all ${theme.transitions.duration.fast} linear`, width: '100%', height: SELECT_HEIGHTS[size] })),
        // @ts-ignore
        control: (provided, state) => {
            isDisabled = isDisabled !== null && isDisabled !== void 0 ? isDisabled : state.isDisabled;
            const styles = Object.assign(Object.assign(Object.assign(Object.assign({}, provided), { flexWrap: isMultiLine ? 'wrap' : 'nowrap', boxShadow: 'none', border: 'none', pointerEvents: 'auto', cursor: isDisabled ? 'not-allowed' : 'pointer', height: getHeight(isMultiLine, size), minHeight: SELECT_HEIGHTS[size] }), getBorderMockJSS(theme)), { backgroundColor: inactive && !isOpen ? 'transparent' : theme.palette[isDisabled ? 'N80' : 'N70'].main, opacity: isDisabled ? '0.4' : undefined, ':hover': {
                    '.react-select__dropdown-indicator': {
                        opacity: 1,
                    },
                    backgroundColor: theme.palette[!isDisabled ? 'N60' : 'N80'].main,
                    '.react-select__placeholder, .react-select__multi-value__label, .react-select__single-value, .react-select__indicator, .react-select__dropdown-indicator': {
                        color: getTextColor({
                            theme,
                            modifiedManually: false,
                            isOpen,
                            isDisabled: isDisabled,
                            onHover: true,
                            containsInvalidValue,
                            warningLevel,
                        }),
                    },
                } });
            if (isDisabled) {
                // @ts-ignore
                styles[':focus-within .react-select__dropdown-indicator'] = { opacity: inactive ? 0 : 1 };
                // @ts-ignore
                styles[':hover .react-select__dropdown-indicator'] = { opacity: inactive ? 0 : 1 };
            }
            if (warningLevel && !isDisabled && !inactive) {
                // @ts-ignore
                styles[':after'].border = `1px solid ${theme.palette[warningLevel].main}`;
            }
            if (!containsInvalidValue && (isOpen || state.isFocused)) {
                // @ts-ignore
                styles[':after'].border = `1px solid ${theme.palette.primary.main}`;
            }
            if (withBorder) {
                styles.border = `1px solid ${theme.palette.N40.main}`;
                // @ts-ignore
                styles[':hover'].border = `1px solid ${theme.palette.N40.main}`;
            }
            return styles;
        },
        // @ts-ignore
        valueContainer: (provided, state) => {
            isDisabled = isDisabled !== null && isDisabled !== void 0 ? isDisabled : state.isDisabled;
            const styles = Object.assign(Object.assign({}, provided), { transition: `color linear ${theme.transitions.duration.fast}`, textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'relative', ':hover div:last-of-type': {
                    color: getTextColor({
                        theme,
                        modifiedManually,
                        isOpen,
                        isDisabled,
                        onHover: true,
                        warningLevel,
                    }),
                }, '.react-select__multi-value__label': {
                    fontSize: 12,
                    padding: theme.spacing(0),
                    color: getTextColor({ theme, modifiedManually, isOpen, isDisabled, warningLevel }),
                }, padding: theme.spacing(0.5, 0, 0.5, size === 'small' ? 1 : size === 'large' ? 2.5 : 2), height: getHeight(isMultiLine, size) });
            if (!state.hasValue) {
                styles['.react-select__multi-value__label'] = Object.assign(Object.assign({}, styles['.react-select__multi-value__label']), { 
                    // @ts-ignore
                    height: getHeight(isMultiLine, size, 4), display: 'flex', alignItems: 'center' });
            }
            if (state.isMulti && state.hasValue) {
                styles.gridTemplateColumns = '1fr auto';
            }
            return styles;
        },
        dropdownIndicator: (provided, state) => (Object.assign(Object.assign({}, provided), { transition: `color linear ${theme.transitions.duration.fast}`, padding: theme.spacing(0.5, size !== 'small' ? 2 : 1), color: getTextColor({
                theme,
                inactive,
                isOpen,
                isDisabled: isDisabled !== null && isDisabled !== void 0 ? isDisabled : state.isDisabled,
                warningLevel,
            }), opacity: inactive && !isOpen ? 0 : 1, pointerEvents: 'none' })),
        input: provided => (Object.assign(Object.assign({}, provided), { margin: 0, padding: 0, color: theme.palette.N05.main })),
        menuPortal: provided => (Object.assign(Object.assign({}, provided), { zIndex: 10001 })),
        // @ts-ignore
        menu: provided => (Object.assign(Object.assign({}, provided), { boxShadow: 'none', zIndex: 15, borderRadius: 4, margin: theme.spacing(0.5, 0), backgroundColor: theme.palette.N40.main })),
        // @ts-ignore
        menuList: provided => (Object.assign(Object.assign({}, provided), { overflowY: 'overlay' })),
        noOptionsMessage: provided => (Object.assign(Object.assign({}, provided), { display: 'flex', fontStyle: 'italic', fontWeight: 'normal', fontSize: 12, opacity: 0.4, padding: theme.spacing(0.5, 0.5, 0.5, 3), color: theme.palette.N20.main })),
        placeholder: (provided, state) => (Object.assign(Object.assign({}, provided), { fontStyle: 'italic', fontWeight: size === 'large' ? 'bold' : 'normal', fontSize: 12, transition: `color linear ${theme.transitions.duration.fast}`, color: theme.palette[error && !(isDisabled !== null && isDisabled !== void 0 ? isDisabled : state.isDisabled) && !isOpen
                ? 'error'
                : warning && !(isDisabled !== null && isDisabled !== void 0 ? isDisabled : state.isDisabled) && !isOpen
                    ? 'warning'
                    : 'N30'].main })),
        indicatorSeparator: provided => (Object.assign(Object.assign({}, provided), { display: 'none' })),
        singleValue: (provided, state) => {
            let styles = Object.assign(Object.assign(Object.assign({}, provided), { padding: 0, fontSize: 12 }), getPlaceholderStyle({
                theme,
                modifiedManually,
                isOpen,
                isDisabled: state.isDisabled,
                warningLevel,
            }));
            if (size === 'large') {
                styles = Object.assign(Object.assign({}, styles), { fontFamily: theme.typography.subtitle1.fontFamily, fontWeight: `${theme.typography.subtitle1.fontWeight} !important`, letterSpacing: theme.typography.subtitle1.letterSpacing, textTransform: theme.typography.subtitle1.textTransform });
            }
            return styles;
        },
    };
};
const useSelectStyles = (options, stylesConfig) => {
    const theme = useTheme();
    return useMemo(() => (Object.assign(Object.assign({}, getSelectStyles(Object.assign({ theme }, options))), stylesConfig)), [theme, stylesConfig, options]);
};

export { useSelectStyles as default };
//# sourceMappingURL=useSelectStyles.js.map
